import { useEffect, useState } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation, useParams } from "react-router-dom";
import { ArrowRight, Check, CircleCheck, Globe2, Leaf, Mail, Menu, MessageCircle, PackageCheck, Phone, Send, ShieldCheck, Users, Wheat, X } from "lucide-react";
import axios from "axios";
import "./App.css";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "https://api.dipaliagro.com").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;
const LOGO = "/assets/dipali-approved-logo.png";
const FOOTER_LOGO = "/assets/footer-logo-transparent-final.png";
const OFFICIAL_PHONE_DISPLAY = "+91 7972018624";
const OFFICIAL_PHONE_HREF = "tel:+917972018624";
const OFFICIAL_EMAIL = "dipaliagro0355@gmail.com";
const WHATSAPP_NUMBER = "917972018624";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Hello%20Dipali%20Agro%20Traders%2C%20I%20would%20like%20to%20make%20an%20inquiry.`;

const images = {
  hero: "/assets/hero-clean.png",
  warehouse: "https://images.pexels.com/photos/32851822/pexels-photo-32851822.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  shipping: "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const products = [
  {
    id: "fresh-green-chilli",
    name: "Fresh Green Chilli",
    category: "Fresh Produce",
    image: "/assets/fresh-green-chilli-approved.png",
    desc: "Farm-fresh green chillies with clear color, natural aroma, and export-quality presentation.",
  },
  {
    id: "turmeric",
    name: "Turmeric",
    category: "Spices",
    image: "/assets/turmeric-approved.png",
    desc: "Fresh turmeric roots and processed turmeric options prepared for buyer purity and color requirements.",
  },
  {
    id: "ginger",
    name: "Ginger",
    category: "Fresh Produce",
    image: "/assets/ginger-approved.png",
    desc: "Fresh Indian ginger roots selected for aroma, texture, and reliable export handling.",
  },
  {
    id: "dry-red-chilli",
    name: "Dry Red Chilli",
    category: "Dry Produce",
    image: "/assets/dry-red-chilli-approved.png",
    desc: "Whole dried red chillies with strong pungency, deep color, and long shelf-life potential.",
  },
  {
    id: "grains-pulses",
    name: "Grains & Pulses",
    category: "Agri Commodities",
    image: "/assets/grains-pulses-approved.png",
    desc: "Clean export-ready grains and pulses sourced from trusted agricultural partners across India.",
  },
];

const processSteps = ["Inquiry", "Quotation", "Order Confirmation", "Quality Check", "Packing & Documentation", "Shipment", "Delivery"];

const createInitialForm = (selectedProduct = "") => ({
  name: "",
  company_name: "",
  email: "",
  phone: "",
  product: selectedProduct,
  quantity: "",
  destination_country: "",
  message: "",
});

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const links = [["/", "Home"], ["/about", "About Us"], ["/products", "Products"], ["/quality", "Quality"], ["/export-process", "Export Process"], ["/contact", "Contact Us"]];

  return (
    <header className="site-header">
      <div className="nav-wrap">
        <Link to="/" className="brand" data-testid="brand-home-link">
          <img src={LOGO} alt="Dipali Agro Traders logo" data-testid="header-logo-image" />
        </Link>

        <button className="menu-toggle" onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Toggle menu">
          {open ? <X /> : <Menu />}
        </button>

        <nav className={open ? "nav-links open" : "nav-links"}>
          {links.map(([to, label]) => (
            <Link
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className={location.pathname === to ? "active" : ""}
              data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}-link`}
            >
              {label}
            </Link>
          ))}

          <Link to="/contact" className="nav-cta" data-testid="header-request-quote-link">
            <MessageCircle size={16} /> Request a Quote
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div>
          <img src={FOOTER_LOGO} className="footer-logo" alt="Dipali Agro Traders" data-testid="footer-logo-image" />
          <p>We are committed to delivering premium agricultural products from India to the world.</p>
          <div className="socials">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link">
              <MessageCircle />
            </a>
            <a href={`mailto:${OFFICIAL_EMAIL}`} data-testid="footer-email-link">
              <Mail />
            </a>
          </div>
        </div>

        <div>
          <h4>Quick Links</h4>
          <Link to="/about" data-testid="footer-about-link">About Us</Link>
          <Link to="/products" data-testid="footer-products-link">Products</Link>
          <Link to="/quality" data-testid="footer-quality-link">Quality & Compliance</Link>
          <Link to="/export-process" data-testid="footer-process-link">Export Process</Link>
        </div>

        <div>
          <h4>Products</h4>
          {products.map((product) => (
            <Link to={`/products/${product.id}`} key={product.id} data-testid={`footer-${product.id}-link`}>
              {product.name}
            </Link>
          ))}
        </div>

        <div>
          <h4>Contact Info</h4>
          <p>
            At Post Palashi,
            <br />
            Taluka Nandurbar,
            <br />
            District Nandurbar,
            <br />
            Maharashtra – 425412, India
          </p>
          <a href={OFFICIAL_PHONE_HREF} data-testid="footer-phone-link">
            <Phone size={15} /> {OFFICIAL_PHONE_DISPLAY}
          </a>
          <a href={`mailto:${OFFICIAL_EMAIL}`} data-testid="footer-contact-email-link">
            <Mail size={15} /> {OFFICIAL_EMAIL}
          </a>
        </div>
      </div>

      <div className="footer-bottom">
        <span>© 2025 Dipali Agro Traders. All Rights Reserved.</span>
        <span>GSTIN: 27CILPP9838C1ZQ &nbsp; | &nbsp; IEC: CILPP9838C</span>
        <span>Designed with <b>♥</b> for quality & trust</span>
      </div>
    </footer>
  );
}

function SectionTitle({ eyebrow, title, copy }) {
  return (
    <div className="section-title">
      <span className="eyebrow">— {eyebrow} —</span>
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

function TrustStrip() {
  return (
    <div className="trust-strip">
      <div><Leaf /><b>Quality Focused</b><span>Carefully sourced</span></div>
      <div><Globe2 /><b>Export Ready</b><span>Global support</span></div>
      <div><PackageCheck /><b>Safe Packaging</b><span>Buyer-ready</span></div>
      <div><Users /><b>Customer Focused</b><span>Clear communication</span></div>
    </div>
  );
}

function ProductCard({ product }) {
  return (
    <article className="product-card" data-testid={`product-card-${product.id}`}>
      <img src={product.image} alt={product.name} data-testid={`product-image-${product.id}`} />
      <div className="product-body">
        <span className="tag">{product.category}</span>
        <h3>{product.name}</h3>
        <p>{product.desc}</p>
        <Link to={`/products/${product.id}`} className="text-link" data-testid={`view-${product.id}-link`}>
          View Details <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}

function Home() {
  return (
    <>
      <Header />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <span className="eyebrow">INDIA • SOURCED WITH CARE</span>
            <h1>Premium Indian <em>Agricultural Products</em> for Global Markets</h1>
            <p>Dipali Agro Traders is a trusted exporter of high-quality agricultural products from India, delivering purity, freshness and reliability to markets worldwide.</p>
            <div className="hero-actions">
              <Link to="/products" className="btn primary" data-testid="hero-explore-products-button">Explore Products <ArrowRight size={17} /></Link>
              <Link to="/contact" className="btn secondary" data-testid="hero-request-quote-button">Request a Quote</Link>
            </div>
          </div>
          <div className="hero-photo" data-testid="hero-product-showcase">
            <img src={images.hero} alt="Approved Dipali Agro export products hero" data-testid="hero-approved-image" />
          </div>
        </section>

        <div className="container"><TrustStrip /></div>

        <section className="section container">
          <SectionTitle eyebrow="Our Products" title="Sourced for the world's tables" copy="Carefully sourced. Carefully processed. Delivered with trust." />
          <div className="product-grid">{products.map((product) => <ProductCard key={product.id} product={product} />)}</div>
          <div className="center">
            <Link to="/products" className="btn primary" data-testid="home-view-all-products-button">View All Products <ArrowRight size={17} /></Link>
          </div>
        </section>

        <section className="why-band">
          <div className="container why-grid">
            <div>
              <span className="eyebrow">WHY DIPALI AGRO</span>
              <h2>Trade with confidence,<br /><em>from farm to port.</em></h2>
              <p>We pair dependable Indian sourcing with transparent communication and buyer-specific preparation.</p>
              <Link to="/about" className="text-link light" data-testid="home-about-link">Discover our approach <ArrowRight size={16} /></Link>
            </div>

            <div className="why-list">
              {[[ShieldCheck, "Premium Quality", "Strict quality checks at every step"], [Phone, "Timely Communication", "Updates you can rely on"], [Wheat, "Competitive Pricing", "Best quality at fair prices"], [PackageCheck, "Custom Packaging", "As per buyer requirements"]].map(([Icon, title, copy]) => (
                <div key={title}>
                  <Icon />
                  <span><b>{title}</b><small>{copy}</small></span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section container">
          <SectionTitle eyebrow="Export Process" title="Simple, transparent & reliable" />
          <div className="process-line">
            {processSteps.map((step, index) => (
              <div className="process-step" key={step}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <b>{step}</b>
                <small>{index === 0 ? "Share your requirement" : index === 6 ? "Products delivered to your port" : "Coordinated with care"}</small>
              </div>
            ))}
          </div>
        </section>

        <section className="markets-band">
          <div className="container markets-content">
            <div>
              <span className="eyebrow">GLOBAL MARKETS</span>
              <h2>Connecting Indian farms<br />with global buyers.</h2>
              <p>We work with importers, distributors, wholesalers and sourcing agents worldwide.</p>
              <div className="market-chips"><span>Middle East</span><span>South East Asia</span><span>Europe</span><span>Africa</span><span>North America</span></div>
            </div>
            <img src={images.shipping} alt="Cargo ship ready for global agricultural exports" data-testid="global-markets-image" />
          </div>
        </section>

        <QuoteForm compact />
      </main>
      <Footer />
      <WhatsApp />
    </>
  );
}

function QuoteForm({ compact = false, selectedProduct = "" }) {
  const [form, setForm] = useState(() => createInitialForm(selectedProduct));
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  useEffect(() => {
    setForm((current) => ({ ...current, product: selectedProduct || current.product }));
  }, [selectedProduct]);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.company_name.trim()) return "Please enter your company name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address.";
    if (!form.phone.trim()) return "Please enter your WhatsApp or phone number.";
    if (!form.product.trim()) return "Please select the product you are interested in.";
    if (!form.quantity.trim()) return "Please enter the estimated quantity.";
    if (!form.destination_country.trim()) return "Please enter the destination country.";
    if (form.message.trim().length < 10) return "Please share a short message with at least 10 characters.";
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();

    if (validationError) {
      setFeedback({ type: "error", message: validationError });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: "", message: "" });

    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
      setForm(createInitialForm(selectedProduct));
    } catch (error) {
      const fallbackMessage = "We couldn't send your enquiry right now. Please try again or contact us on WhatsApp.";
      const backendMessage = error.response?.status === 502 ? error.response?.data?.detail : "";
      setFeedback({ type: "error", message: backendMessage || fallbackMessage });
      setSent(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSent(false);
    setFeedback({ type: "", message: "" });
    setForm(createInitialForm(selectedProduct));
  };

  return (
    <section className={compact ? "quote-section" : "section container"}>
      <div className={compact ? "container quote-wrap" : "quote-wrap standalone"}>
        <div className="quote-intro">
          <span className="eyebrow">START A CONVERSATION</span>
          <h2>Request a <em>Quote</em></h2>
          <p>Tell us what you need. Our export team will get back to you with the right details.</p>

          <div className="contact-note">
            <MessageCircle />
            <span>
              <b>Prefer WhatsApp?</b>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="quote-whatsapp-link">Chat with our team directly</a>
            </span>
          </div>

          <div className="contact-pills">
            <a className="contact-pill" href={OFFICIAL_PHONE_HREF} data-testid="quote-phone-link"><Phone size={16} /> {OFFICIAL_PHONE_DISPLAY}</a>
            <a className="contact-pill" href={`mailto:${OFFICIAL_EMAIL}`} data-testid="quote-email-link"><Mail size={16} /> {OFFICIAL_EMAIL}</a>
          </div>
        </div>

        {sent ? (
          <div className="success-box" data-testid="inquiry-success-message">
            <CircleCheck size={42} />
            <h3>Enquiry received</h3>
            <p>Thank you for your enquiry! Our team has received your request and will get back to you shortly.</p>
            <button className="btn primary" onClick={resetForm} data-testid="send-another-inquiry-button">Send another inquiry</button>
          </div>
        ) : (
          <form className="quote-form" onSubmit={submit} noValidate data-testid="quote-form">
            {feedback.type === "error" && <div className="form-feedback error" role="alert" data-testid="inquiry-error-message">{feedback.message}</div>}

            <input required name="name" placeholder="Full Name *" value={form.name} onChange={update} data-testid="quote-name-input" />
            <input required name="company_name" placeholder="Company Name *" value={form.company_name} onChange={update} data-testid="quote-company-input" />
            <input required type="email" name="email" placeholder="Email Address *" value={form.email} onChange={update} data-testid="quote-email-input" />
            <input required name="phone" placeholder="WhatsApp / Phone Number *" value={form.phone} onChange={update} data-testid="quote-phone-input" />

            <select required name="product" value={form.product} onChange={update} data-testid="quote-product-select">
              <option value="">Product Interested In *</option>
              {products.map((product) => <option value={product.name} key={product.id}>{product.name}</option>)}
            </select>

            <input required name="quantity" placeholder="Estimated Quantity *" value={form.quantity} onChange={update} data-testid="quote-quantity-input" />
            <input required name="destination_country" placeholder="Destination Country *" value={form.destination_country} onChange={update} data-testid="quote-destination-input" />
            <textarea required name="message" placeholder="Message *" value={form.message} onChange={update} data-testid="quote-message-input" />

            <button className="btn primary submit" type="submit" disabled={isSubmitting} data-testid="quote-submit-button">
              <Send size={16} /> {isSubmitting ? "Sending..." : "Send Inquiry"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function PageHero({ label, title, copy }) {
  return (
    <section className="page-hero">
      <div className="container">
        <span className="eyebrow">{label}</span>
        <h1>{title}</h1>
        <p>{copy}</p>
      </div>
    </section>
  );
}

function Products() {
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...new Set(products.map((product) => product.category))];

  return (
    <>
      <Header />
      <PageHero label="OUR CATALOGUE" title="Products you can source with confidence." copy="Focused Indian agricultural products, prepared around your market and requirements." />
      <main className="section container">
        <div className="filters">
          {categories.map((category) => (
            <button className={filter === category ? "filter active" : "filter"} onClick={() => setFilter(category)} key={category} data-testid={`filter-${category.toLowerCase().replaceAll(" ", "-")}-button`}>
              {category}
            </button>
          ))}
        </div>

        <div className="product-grid catalogue">
          {products.filter((product) => filter === "All" || product.category === filter).map((product) => <ProductCard key={product.id} product={product} />)}
        </div>
      </main>
      <Footer />
      <WhatsApp />
    </>
  );
}

function ProductDetail() {
  const { id } = useParams();
  const product = products.find((entry) => entry.id === id) || products[0];

  return (
    <>
      <Header />
      <main>
        <section className="detail container">
          <div className="detail-image"><img src={product.image} alt={product.name} data-testid="product-detail-image" /></div>

          <div className="detail-copy">
            <span className="tag">{product.category}</span>
            <h1>{product.name}</h1>
            <p>{product.desc} We prepare each order with clear specifications and buyer-focused support.</p>
            <div className="spec-table">
              <div><span>Origin</span><b>India</b></div>
              <div><span>Availability</span><b>Seasonal / on request</b></div>
              <div><span>Packaging</span><b>Customizable</b></div>
              <div><span>MOQ</span><b>On request</b></div>
            </div>
            <Link to={`/contact?product=${product.name}`} className="btn primary" data-testid="product-request-quote-button">Request a Quote <ArrowRight size={17} /></Link>
          </div>
        </section>

        <section className="soft-section">
          <div className="container detail-bottom">
            <SectionTitle eyebrow="Buyer Support" title="Prepared for your market." copy="Share your required grade, quantity, packaging and destination. We’ll help you shape the right inquiry." />
            <Link to="/contact" className="btn secondary" data-testid="product-discuss-requirements-button">Discuss Requirements</Link>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsApp />
    </>
  );
}

function StandardPage({ type }) {
  const content = {
    about: ["ABOUT DIPALI AGRO", "A dependable partner for Indian agricultural sourcing.", "We connect trusted Indian farms and processing partners with buyers looking for consistency, clear communication and export-ready preparation."],
    quality: ["QUALITY & COMPLIANCE", "Quality is a process, not a promise.", "From supplier selection to pre-shipment checks, we keep each order focused on the standards and destination requirements that matter."],
    process: ["EXPORT PROCESS", "From your inquiry to your port.", "A straightforward workflow keeps every stage visible, coordinated and focused on your requirements."],
  }[type];

  return (
    <>
      <Header />
      <PageHero label={content[0]} title={content[1]} copy={content[2]} />
      <main>
        {type === "about" ? (
          <section className="section container split">
            <img src={images.warehouse} alt="Agricultural products being prepared for export" data-testid="about-page-image" />
            <div>
              <span className="eyebrow">OUR APPROACH</span>
              <h2>Indian sourcing, handled with care.</h2>
              <p>Dipali Agro Traders is built around a simple belief: international buyers deserve reliable products, honest timelines and a responsive partner.</p>
              <div className="check-list">
                <p><Check />Sourced from trusted agricultural networks</p>
                <p><Check />Clear product and packing conversations</p>
                <p><Check />Support from inquiry through shipment</p>
              </div>
              <Link to="/contact" className="btn primary" data-testid="about-request-quote-button">Request a Quote <ArrowRight size={17} /></Link>
            </div>
          </section>
        ) : type === "quality" ? (
          <section className="section container">
            <div className="quality-grid">
              {[[ShieldCheck, "Supplier Selection", "We work with sources aligned to the product and destination requirement."], [CircleCheck, "Inspection & Grading", "Products are checked, sorted and prepared with care."], [PackageCheck, "Packing", "Packaging is discussed around safe handling and buyer needs."], [Globe2, "Credentials", "IEC, GST and applicable registrations are represented accurately."]].map(([Icon, title, copy]) => (
                <div className="quality-card" key={title}>
                  <Icon />
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </div>
              ))}
            </div>
            <div className="credentials">
              <span className="eyebrow">CREDENTIALS</span>
              <h2>Business-ready, buyer-focused.</h2>
              <p>IEC: CILPP9838C &nbsp; • &nbsp; GSTIN: 27CILPP9838C1ZQ</p>
            </div>
          </section>
        ) : (
          <section className="section container">
            <div className="vertical-process">
              {processSteps.map((step, index) => (
                <div key={step}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h3>{step}</h3>
                    <p>
                      {index === 0 ? "Share your product, quantity and destination requirement." : index === 1 ? "We provide a clear, buyer-specific quotation." : index === 2 ? "Confirm the order with agreed terms." : index === 3 ? "Quality inspection and packing are coordinated." : index === 4 ? "Safe packing and export documents are prepared." : index === 5 ? "Shipment moves with regular communication." : "Products reach your nominated port."}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="center"><Link to="/contact" className="btn primary" data-testid="process-start-inquiry-button">Start an Inquiry <ArrowRight size={17} /></Link></div>
          </section>
        )}
      </main>
      <Footer />
      <WhatsApp />
    </>
  );
}

function Contact() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  return (
    <>
      <Header />
      <PageHero label="CONTACT / REQUEST A QUOTE" title="Let’s talk about your requirement." copy="Tell us what you’re sourcing and where it needs to go. We’ll get back to you with the right next step." />
      <QuoteForm selectedProduct={params.get("product") || ""} />
      <Footer />
      <WhatsApp />
    </>
  );
}

function WhatsApp() {
  return (
    <a className="whatsapp" href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="floating-whatsapp-button">
      <MessageCircle />
      <span>Chat on WhatsApp</span>
    </a>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/about" element={<StandardPage type="about" />} />
        <Route path="/quality" element={<StandardPage type="quality" />} />
        <Route path="/export-process" element={<StandardPage type="process" />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
